/**
 * Shared game types. This file is the contract between the rules engine and
 * everything that renders it. Ported from the original React Native Wild21
 * (src/screens/GameBoard/tableau.js + src/utils/CommonData.js ScoreManager).
 */

export type Suit = 'c' | 'd' | 'h' | 's';
export type Rank = 'a' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'j' | 'q' | 'k';

/** Card ids match the original asset naming: `as`, `10d`, `kh`, `jc`. */
export type CardId = string;

/** The two wild cards: Jack of spades and Jack of clubs. */
export const WILD_CARDS: readonly CardId[] = ['js', 'jc'] as const;

export const PILE_COUNT = 4;
export const MAX_BUSTS = 3;
export const ROUND_SECONDS = 180;

/**
 * Every round is one deck and three minutes: `ROUND_SECONDS`. (Challenges once came in 3, 6 and 9 minutes, with a
 * deck per three minutes; that was removed in full on 2026-10-11, Adam: "3 min is the best" and they are not coming
 * back. The columns `challenges.duration_ms` and `results.duration_ms` stay in the database, unread, and every
 * existing row is a 3:00 game.)
 */

/** Points table — matches ScoreManager.resetScore() in the original. */
export const POINTS = {
  twentyOne: 400,
  fiveCard: 600,
  wild: 200,
  perfectGame: 1000,
  noBust: 100,
  /** Streak bonuses indexed by streak level 1..5, rendered as 2x..6x. */
  streak: [250, 500, 750, 1000, 1250],
  /** Base time-bonus rate, decayed by 0.2 per card left unplayed. */
  timeBonusRate: 10,
  timeBonusDecayPerCard: 0.2,
} as const;

export const STREAK_LABELS = ['2x', '3x', '4x', '5x', '6x'] as const;

/**
 * A pile's two running totals.
 * `hard` counts every ace as 1. `soft` counts the FIRST ace as 11 and any
 * further aces as 1 — this mirrors the original scoring loop exactly.
 */
export interface PileTotals {
  hard: number;
  soft: number;
}

export type BonusKind = 'twentyOne' | 'fiveCard' | 'wild';

/** The outcome of dropping one card onto one pile. */
/**
 * A card the player is not allowed to know.
 *
 * In a ranked round the deck lives on the server and the client is told only
 * the face-up card — but the renderer still has to draw the stack underneath
 * it, and every sprite is checked out of the pool by card id. These are the
 * ids it uses for those: unique, so two hidden cards never share a sprite, and
 * outside the real deck's namespace, so one can never collide with a card that
 * is genuinely on the table.
 *
 * They are only ever drawn face down. Asking the atlas for the face of one
 * yields the back — see `getFaceUV` — which is the truthful answer rather than
 * a fallback.
 */
export function hiddenCardId(index: number): CardId {
  return `?${index}`;
}

/** Whether `id` is a placeholder for a card the client has not been shown. */
export function isHiddenCard(id: CardId): boolean {
  return id.charCodeAt(0) === 63; // '?'
}

export interface Resolution {
  /** Pile index the card landed on. */
  pile: number;
  /** The card that was played. */
  card: CardId;
  /** Cards in the pile at the moment of resolution (including `card`). */
  cards: CardId[];
  totals: PileTotals;
  /** True when the pile empties as a result of this play. */
  cleared: boolean;
  is21: boolean;
  isFiveCard: boolean;
  isWild: boolean;
  isBust: boolean;
  /** Bonuses earned, in display order. */
  bonuses: BonusKind[];
  /** Points from bonuses, excluding streak. */
  basePoints: number;
  /** Streak level 0 = none, 1..5 = the 2x..6x callouts. */
  streakLevel: number;
  streakPoints: number;
  streakLabel: string | null;
  /** basePoints + streakPoints */
  pointsGained: number;
  /** Running score after this resolution. */
  score: number;
  /** Running bust count after this resolution. */
  busts: number;
}

export type GamePhase = 'idle' | 'dealing' | 'playing' | 'resolving' | 'over';

export type GameOverReason = 'deckEmpty' | 'bustedOut' | 'timeout';

export interface FinalScore {
  /** Score before end-of-game bonuses. */
  base: number;
  perfectGameBonus: number;
  noBustBonus: number;
  timeBonus: number;
  total: number;
  busts: number;
  secondsRemaining: number;
  cardsRemaining: number;
  perfectGame: boolean;
  reason: GameOverReason;
  /** Piles cleared — 21 Wild's repeatable unit of progress. */
  clears: number;
  /** Highest streak level reached, 0..5 (the 2x..6x callouts). */
  bestStreak: number;
  /** Cards played. Zero means the round happened without the player. */
  plays: number;
  /**
   * The level reached during play.
   *
   * Read off the in-play score, so the end-of-game bonuses below it — perfect
   * game, no-bust, time — do NOT raise it. A level is a record of how the round
   * was played, not of how it was totted up afterwards.
   */
  levelReached: number;
  /**
   * Set when this end could not get the round's own breakdown (the server no longer held the session and only
   * its recorded score and level were left). Only `total` and `levelReached` are real; the results
   * screen shows the score and the verdict and hides the rows.
   */
  detailsUnavailable?: boolean;
}

export interface GameSnapshot {
  phase: GamePhase;
  /** Face-up card waiting to be placed, or null when the stock is empty. */
  activeCard: CardId | null;
  /** Cards still in the stock, including `activeCard`. */
  stock: CardId[];
  piles: CardId[][];
  pileTotals: PileTotals[];
  score: number;
  busts: number;
  secondsRemaining: number;
  streakLevel: number;
  /**
   * The level the in-play score has reached, 1 at the start.
   *
   * Derived from `score` rather than stored alongside it. That is deliberate:
   * a level held as its own field is a second copy of a fact the score already
   * carries, and every path that rewinds the score — undo, a ranked recovery
   * resync, a replay — would have to remember to rewind it too. Deriving makes
   * those paths correct without their knowing levels exist at all.
   */
  level: number;
  canUndo: boolean;
}

/** Events the engine emits. The renderer subscribes; it never mutates state. */
export type GameEvent =
  | { type: 'game:start'; snapshot: GameSnapshot }
  | { type: 'card:play'; pile: number; card: CardId; snapshot: GameSnapshot }
  | { type: 'pile:resolve'; resolution: Resolution; snapshot: GameSnapshot }
  | { type: 'score:change'; score: number; delta: number; snapshot: GameSnapshot }
  /**
   * The in-play score crossed a level threshold. Fired immediately after the
   * `score:change` that caused it — one event per play, even when a single
   * scoring move jumps two levels, carrying the level actually arrived at.
   */
  | {
      type: 'level:up';
      level: number;
      from: number;
      atScore: number;
      nextThreshold: number;
      snapshot: GameSnapshot;
    }
  | { type: 'bust'; pile: number; busts: number; snapshot: GameSnapshot }
  | { type: 'tick'; secondsRemaining: number; snapshot: GameSnapshot }
  | { type: 'undo'; snapshot: GameSnapshot }
  | { type: 'game:over'; final: FinalScore; snapshot: GameSnapshot };
